import React, { useState, useEffect, useRef } from 'react';
import { Device } from '@twilio/voice-sdk';
import axios from '@/lib/axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Phone } from 'lucide-react';

const InAppCall = ({ communityId }) => {
    const deviceRef = useRef(null);
    const [isDeviceReady, setIsDeviceReady] = useState(false);
    const [activeCall, setActiveCall] = useState(null);
    const [isDialPadOpen, setIsDialPadOpen] = useState(false);
    const [dialpad, setDialpad] = useState('');

    const fetchToken = async () => {
        const { data } = await axios.post('/secret-shopper/call-token');
        return data;
    };


    const handleTokenWillExpire = async () => {
        console.log('Token is about to expire. Fetching a new one...');
        try {
            if (!deviceRef.current) {
                return;
            }

            const { token } = await fetchToken();
            deviceRef.current.updateToken(token);
            console.log('Token refreshed and device updated');
        } catch (error) {
            console.error('Failed to refresh token:', error);
        }
    };

    useEffect(() => {
        if (!isDialPadOpen && activeCall) {
            activeCall.disconnect();
        }
    }, [isDialPadOpen]);

    // Initialize the device on component mount
    useEffect(() => {
        const initializeDevice = async () => {
            try {
                const { token } = await fetchToken();

                const newDevice = new Device(token, {
                    codecPreferences: ['opus', 'pcmu'],
                    enableRingingState: true,
                    enableImprovedSignalingErrorPrecision: true,
                });

                newDevice.on('error', (error) => {
                    console.error('Twilio Device Error:', error);
                });

                newDevice.on('tokenWillExpire', handleTokenWillExpire);
                newDevice.register();
                deviceRef.current = newDevice;

                setIsDeviceReady(true);

                console.log('Device initialized and registered:', newDevice);
            } catch (error) {
                console.error('Failed to initialize device:', error);
            }
        };

        initializeDevice();

        return () => {
            if (deviceRef.current) {
                deviceRef.current.destroy();
                deviceRef.current = null;
                console.log('Device destroyed');
            }
        };
    }, []);

    const makeCall = async () => {
        if (deviceRef.current) {
            try {
                const call = await deviceRef.current.connect({ params: { community_id: communityId } });
                if (call) {
                    call.on('disconnect', () => {
                        console.log('Call Disconnected');
                        setActiveCall(null);
                        setIsDialPadOpen(false);
                        setDialpad('');
                    });

                    setActiveCall(call);
                    console.log('Call initiated:', call);
                } else {
                    console.error('Failed to initiate call.');
                }
            } catch (error) {
                console.error(error)
            }
        } else {
            console.error('Device not initialized.');
        }
    };

    const sendDTMF = (tone) => {
        if (activeCall) {
            activeCall.sendDigits(tone);
            setDialpad(`${dialpad}${tone}`);
            console.log(`DTMF tone sent: ${tone}`);
        } else {
            console.error('No active call to send DTMF tones.');
        }
    };

    const endCall = () => {
        if (activeCall) {
            activeCall.disconnect();
            console.log('Call ended');
        }
    };

    return (
        <Dialog open={isDialPadOpen} onOpenChange={setIsDialPadOpen}>
            <DialogTrigger disabled={!isDeviceReady} asChild>
                <Button disabled={!isDeviceReady} variant="outline">
                    <>
                        <Phone className="h-4 w-4 mr-2" />
                        Call Property
                    </>
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle className="mb-4 text-center">
                        {!activeCall && `Are you sure you want to call the community via the prospect's phone number?`}
                        {!!activeCall && (dialpad || 'Dialpad')}
                    </DialogTitle>
                    <DialogDescription className="space-y-2 text-center">
                        {!activeCall &&
                            <>
                                <Button
                                    onClick={makeCall}
                                >
                                    Confirm
                                </Button>
                                <Button variant="ghost" onClick={() => setIsDialPadOpen(false)}>
                                    Cancel
                                </Button>
                            </>
                        }

                        {!!activeCall &&
                            <>
                                <h3>Exiting out of this modal will end the call.</h3>
                                <div className="grid grid-cols-3 gap-2">
                                    {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((digit) => (
                                        <Button
                                            key={digit}
                                            onClick={() => sendDTMF(digit)}
                                            className="p-4 text-xl font-bold"
                                        >
                                            {digit}
                                        </Button>
                                    ))}
                                </div>
                                <Button variant="destructive" onClick={endCall}>
                                    End Call
                                </Button>
                            </>
                        }
                    </DialogDescription>
                </DialogHeader>
            </DialogContent>
        </Dialog>
    );
};

export default InAppCall;
